import { Helmet } from "react-helmet-async";

type Plan = {
  code: string;
  name: string;
  audience: "private" | "dealer";
  price: number;
  currency: string;
  unit: string;
  features: string[];
};

const PRIVATE_PLANS: Plan[] = [
  { code: "standard",   name: "Standard",              audience: "private", price: 9.99,  currency: "EUR", unit: "per listing", features: ["20 fotografii", "30 zile activ", "Prioritate ușoară în listă timp de 30 de zile"] },
  { code: "promoted",   name: "Promovat",              audience: "private", price: 14.99, currency: "EUR", unit: "per listing", features: ["Insignă Promovat", "Afișare prioritară 30 zile"] },
  { code: "until_sold", name: "Activ până la vânzare", audience: "private", price: 19.99, currency: "EUR", unit: "per month",   features: ["Reînnoire automată lunară", "Activ până marchezi vândut"] },
];

const DEALER_PLANS: Plan[] = [
  { code: "start",    name: "Start",    audience: "dealer", price: 29.99,  currency: "EUR", unit: "per month", features: ["Până la 5 anunțuri active", "Import CSV / XML", "0% comision"] },
  { code: "standard", name: "Standard", audience: "dealer", price: 50,     currency: "EUR", unit: "per month", features: ["Până la 60 anunțuri active", "1 anunț promovat"] },
  { code: "large",    name: "Large",    audience: "dealer", price: 120,    currency: "EUR", unit: "per month", features: ["Anunțuri nelimitate", "3 anunțuri promovate", "Suport prioritar"] },
];

interface Props {
  /** Which set of plans to expose. "all" includes both private and dealer. */
  audience?: "private" | "dealer" | "all";
  /** Override currency & prices for regional pages (UK/DE). */
  overrides?: Plan[];
  /** Canonical URL of the page (used in @id). */
  pageUrl: string;
}

const ORG_ID = "https://roautomarket.ro/#organization";
const ORG_LOGO = "https://roautomarket.ro/og-roautomarket.png";

/**
 * Emits a single schema.org Product with an array of Offers, referencing a
 * shared Organization as brand + seller. Also renders a hidden semantic HTML
 * pricing table so AI agents and crawlers (Perplexity, ChatGPT, Google) can
 * read pricing without executing JavaScript-heavy components.
 *
 * Notes:
 * - Prices are exclusive of VAT (valueAddedTaxIncluded=false). VAT applies per
 *   country legislation (RO 21%, DE 19%, UK 20%, FR 20%).
 */
export function PricingSchema({ audience = "all", overrides, pageUrl }: Props) {
  const plans: Plan[] = overrides
    ? overrides
    : audience === "private"
      ? PRIVATE_PLANS
      : audience === "dealer"
        ? DEALER_PLANS
        : [...PRIVATE_PLANS, ...DEALER_PLANS];

  const offers = plans.map((p) => {
    const isSubscription = p.unit === "per month";
    const priceSpec: Record<string, unknown> = {
      "@type": "UnitPriceSpecification",
      price: p.price.toFixed(2),
      priceCurrency: p.currency,
      unitText: p.unit,
      valueAddedTaxIncluded: false,
    };
    if (isSubscription) {
      priceSpec.billingDuration = "P1M";
    }

    return {
      "@type": "Offer",
      "@id": `${pageUrl}#${p.audience}-${p.code}`,
      name: `${p.name} — ${p.audience === "dealer" ? "Dealer" : "Persoană fizică"}`,
      description: p.features.join(". "),
      url: pageUrl,
      category: p.audience === "dealer" ? "Dealeri" : "Persoane fizice",
      eligibleCustomerType: p.audience === "dealer" ? "Business" : "Enduser",
      availability: "https://schema.org/InStock",
      priceSpecification: priceSpec,
      seller: { "@id": ORG_ID },
    };
  });

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Prețuri RO Auto Market",
        description: "Pachete pentru persoane fizice și dealeri pe RO Auto Market.",
      },
      {
        "@type": "Organization",
        "@id": ORG_ID,
        name: "RO Auto Market",
        url: "https://roautomarket.ro/",
        logo: ORG_LOGO,
      },
      {
        "@type": "Product",
        "@id": `${pageUrl}#product`,
        name: "Pachete RO Auto Market",
        url: pageUrl,
        description: "Pachete de publicare pentru persoane fizice și dealeri.",
        brand: { "@id": ORG_ID },
        seller: { "@id": ORG_ID },
        offers,
      },
    ],
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      </Helmet>

      {/* Machine-readable, screen-reader-friendly pricing table.
          Rendered in DOM (not display:none) so AI crawlers see it,
          but visually hidden with sr-only utility. */}
      <section aria-label="Pricing summary for AI agents" className="sr-only">
        <h2>Prețuri RO Auto Market</h2>
        <p>
          Toate prețurile sunt fără TVA. TVA aplicat conform legislației
          (RO 21%, DE 19%, UK 20%, FR 20%). Plata prin Stripe. Anulare oricând.
          Feed complet: <a href="/pricing.json">/pricing.json</a>.
        </p>
        <table>
          <thead>
            <tr>
              <th>Pachet</th>
              <th>Tip</th>
              <th>Preț</th>
              <th>Monedă</th>
              <th>Unitate</th>
              <th>Include</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={`${p.audience}-${p.code}`}>
                <td>{p.name}</td>
                <td>{p.audience === "dealer" ? "Dealer" : "Persoană fizică"}</td>
                <td>{p.price.toLocaleString('ro-RO', { minimumFractionDigits: p.price % 1 ? 2 : 0, maximumFractionDigits: 2 })}</td>
                <td>{p.currency}</td>
                <td>{p.unit === 'per listing' ? 'per anunț' : 'pe lună'}</td>
                <td>{p.features.join("; ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
